import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiOff, Loader2, Check } from 'lucide-react';
import { onSyncStatusChange, getSyncStatus } from '../lib/syncManager';

export default function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState(getSyncStatus());
  const [showSynced, setShowSynced] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    return onSyncStatusChange((status) => {
      setSyncStatus(status);
      if (status === 'synced') {
        setShowSynced(true);
        setTimeout(() => setShowSynced(false), 3000);
      }
    });
  }, []);

  // Don't show anything when online and idle
  if (isOnline && syncStatus === 'idle' && !showSynced) return null;
  if (isOnline && syncStatus === 'synced' && !showSynced) return null;

  return (
    <AnimatePresence>
      {(!isOnline || syncStatus === 'syncing' || showSynced) && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className={`px-4 py-2 flex items-center gap-2 text-sm ${
            !isOnline
              ? 'bg-gold-bright/10 border-b border-gold-bright/30 text-gold-bright'
              : syncStatus === 'syncing'
              ? 'bg-flow-blue/10 border-b border-flow-blue/30 text-flow-blue-hover'
              : 'bg-teal-flow/10 border-b border-teal-flow/30 text-teal-flow'
          }`}
        >
          {!isOnline && (
            <>
              <WifiOff size={16} />
              Offline — changes saved locally
            </>
          )}
          {isOnline && syncStatus === 'syncing' && (
            <>
              <Loader2 size={16} className="animate-spin" />
              Syncing changes...
            </>
          )}
          {isOnline && showSynced && syncStatus !== 'syncing' && (
            <>
              <Check size={16} />
              All changes saved
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
